import { Link } from 'react-router';
import { Card, cx, Empty, ErrorNote, Loading } from '../components/ui';
import type { Datapoint, LatestValue } from '../lib/api';
import { datapointLabel, fmtAgo, fmtTime, fmtValue, humanize } from '../lib/format';
import { useAlarms, useDatapoints, useLatest } from '../lib/queries';
import { ActiveAlarms } from './PlantAlarms';
import { usePlantContext } from './Plant';

const TONE: Record<string, string> = { normal: '', warning: 'text-medium', critical: 'text-critical font-semibold' };
const STALE_MS = 15 * 60_000;

export function PlantOverview() {
  const plant = usePlantContext();
  const datapoints = useDatapoints(plant.slug);
  const latest = useLatest(plant.slug);
  const active = useAlarms({ plant: plant.slug, active: 'true' });

  if (datapoints.isPending || latest.isPending) return <Loading />;
  if (datapoints.error || latest.error) return <ErrorNote error={datapoints.error ?? latest.error} />;
  if (datapoints.data.length === 0) return <Empty>No datapoints yet. They appear when the plant's edge device sends its catalog.</Empty>;

  const byId = new Map(latest.data.map((l) => [l.datapointId, l]));
  const assets = new Map<string, Datapoint[]>();
  for (const d of datapoints.data) {
    if (d.category === 'alarm') continue;
    const list = assets.get(d.asset) ?? [];
    list.push(d);
    assets.set(d.asset, list);
  }

  return (
    <>
      {active.data && active.data.length > 0 && (
        <div className="mb-6">
          <h2 className="mb-2 text-base font-semibold">Active alarms</h2>
          <ActiveAlarms alarms={active.data} timezone={() => plant.timezone} showPlant={false} />
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {[...assets.entries()].map(([asset, dps]) => (
          <Card key={asset} className="p-4">
            <h2 className="mb-2 text-base font-semibold">{humanize(asset)}</h2>
            <table className="w-full text-sm">
              <tbody>
                {dps
                  .sort((a, b) => (a.category === b.category ? datapointLabel(a).localeCompare(datapointLabel(b)) : a.category === 'state' ? -1 : 1))
                  .map((d) => <Row key={d.id} d={d} latest={byId.get(d.id)} plant={plant.slug} timezone={plant.timezone} />)}
              </tbody>
            </table>
          </Card>
        ))}
      </div>
    </>
  );
}

function Row({ d, latest, plant, timezone }: { d: Datapoint; latest?: LatestValue; plant: string; timezone: string }) {
  const value = latest ? fmtValue(d, latest.value, latest.text) : { text: '–', tone: 'normal' as const };
  // On-change datapoints (states, flags) keep their last value for as long as nothing changes.
  const stale = latest && !d.meta.storageOnChangeOnly && Date.now() - Date.parse(latest.time) > STALE_MS;
  return (
    <tr className="border-t border-rule first:border-t-0">
      <td className="py-1.5 pr-3">
        <Link to={`/plants/${plant}/charts?dp=${d.id}`} className="hover:text-accent hover:underline" title={d.topic}>{datapointLabel(d)}</Link>
      </td>
      <td className={cx('tabular py-1.5 text-right', TONE[value.tone])}>{value.text}</td>
      <td className="w-20 py-1.5 pl-3 text-right text-xs text-muted" title={latest ? fmtTime(latest.time, timezone, { seconds: true }) : undefined}>
        {latest ? (stale ? <span className="text-medium">{fmtAgo(latest.time)}</span> : fmtAgo(latest.time)) : 'no data'}
      </td>
    </tr>
  );
}
